import { useEffect, useRef, useState } from 'react'
import { api, type Track } from './api.ts'
import { useAuth } from './AuthContext.tsx'
import { dspscript } from './dsp-script.ts'
import { navigate } from './Router.tsx'
import { showToast } from './Toast.tsx'
import { useChannelModeStore, useLatencyHintStore, useSessionStore } from './store.ts'

type PageType = 'publicTracks' | 'trackPage' | 'profile'

interface UseTrackListOptions {
  pageType: PageType
  tracks: Track[]
  loading: boolean
  onTracksUpdate?: (tracks: Track[]) => void
  onSingleTrackUpdate?: (track: Track | null) => void
}

export function useTrackList({
  pageType,
  tracks,
  loading,
  onTracksUpdate,
  onSingleTrackUpdate,
}: UseTrackListOptions) {
  const { user } = useAuth()
  const { channelMode } = useChannelModeStore()
  const { latencyHint } = useLatencyHintStore()
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null)
  const [actuallyPlaying, setActuallyPlaying] = useState(false)
  const [trackParents, setTrackParents] = useState<Record<string, Track>>({})
  const [trackRemixes, setTrackRemixes] = useState<Record<string, Track[]>>({})
  const [likedTrackIds, setLikedTrackIds] = useState<Set<string>>(new Set())
  const [activeRemixId, setActiveRemixId] = useState<string | null>(null)
  const [compileMode] = useState<'key' | 'ctrl+enter'>('key')
  const isTogglingLikeRef = useRef(false)

  const sessionStore = useSessionStore()
  const getActiveTrackId = () => {
    switch (pageType) {
      case 'publicTracks':
        return sessionStore.getPublicTracksActiveTrackId()
      case 'trackPage':
        return sessionStore.getTrackPageActiveTrackId()
      case 'profile':
        return sessionStore.getProfileActiveTrackId()
    }
  }
  const getActiveRemixId = () => {
    switch (pageType) {
      case 'publicTracks':
        return sessionStore.getPublicTracksActiveRemixId()
      case 'trackPage':
        return sessionStore.getTrackPageActiveRemixId()
      case 'profile':
        return sessionStore.getProfileActiveRemixId()
    }
  }
  const setActiveTrackIdInStore = (id: string | null) => {
    switch (pageType) {
      case 'publicTracks':
        return sessionStore.setPublicTracksActiveTrackId(id)
      case 'trackPage':
        return sessionStore.setTrackPageActiveTrackId(id)
      case 'profile':
        return sessionStore.setProfileActiveTrackId(id)
    }
  }
  const setActiveRemixIdInStore = (id: string | null) => {
    switch (pageType) {
      case 'publicTracks':
        return sessionStore.setPublicTracksActiveRemixId(id)
      case 'trackPage':
        return sessionStore.setTrackPageActiveRemixId(id)
      case 'profile':
        return sessionStore.setProfileActiveRemixId(id)
    }
  }
  const removeActiveTrackId = () => {
    switch (pageType) {
      case 'publicTracks':
        return sessionStore.removePublicTracksActiveTrackId()
      case 'trackPage':
        return sessionStore.removeTrackPageActiveTrackId()
      case 'profile':
        return sessionStore.removeProfileActiveTrackId()
    }
  }
  const removeActiveRemixId = () => {
    switch (pageType) {
      case 'publicTracks':
        return sessionStore.removePublicTracksActiveRemixId()
      case 'trackPage':
        return sessionStore.removeTrackPageActiveRemixId()
      case 'profile':
        return sessionStore.removeProfileActiveRemixId()
    }
  }

  useEffect(() => {
    const playing = dspscript.isPlaying()
    if (playing) {
      setActuallyPlaying(true)
    }

    const interval = setInterval(() => {
      const currentlyPlaying = dspscript.isPlaying()
      setActuallyPlaying(currentlyPlaying)
    }, 500)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (tracks.length === 0 || loading) return

    const playing = dspscript.isPlaying()
    const editorInitiated = sessionStore.getEditorInitiatedPlayback()
    const storedActiveTrackId = getActiveTrackId()
    const storedActiveRemixId = getActiveRemixId()

    if (editorInitiated) {
      if (storedActiveTrackId) {
        removeActiveTrackId()
      }
      if (storedActiveRemixId) {
        removeActiveRemixId()
      }
      sessionStore.removeEditorInitiatedPlayback()
      return
    }

    if (playing && (storedActiveTrackId || storedActiveRemixId)) {
      if (storedActiveTrackId) {
        const trackExists = tracks.some(t => t.id === storedActiveTrackId)
        if (trackExists) {
          setActiveTrackId(storedActiveTrackId)
          setActiveRemixId(null)
        } else {
          removeActiveTrackId()
        }
      }

      if (storedActiveRemixId) {
        const remixExists = Object.values(trackRemixes).some(remixes =>
          remixes.some(r => r.id === storedActiveRemixId),
        )
        if (remixExists) {
          setActiveRemixId(storedActiveRemixId)
          setActiveTrackId(null)
        } else if (Object.keys(trackRemixes).length > 0) {
          removeActiveRemixId()
        }
      }
    }
  }, [tracks, loading, trackRemixes])

  const loadTrackData = (tracksToProcess: Track[], append = false) => {
    const parentsMap: Record<string, Track> = append ? { ...trackParents } : {}
    const remixesMap: Record<string, Track[]> = append ? { ...trackRemixes } : {}
    const likedIds = new Set(likedTrackIds)

    for (const track of tracksToProcess) {
      if (track.parentTrack) {
        parentsMap[track.id] = track.parentTrack
        if (track.parentTrack.isLiked !== undefined) {
          if (track.parentTrack.isLiked) {
            likedIds.add(track.parentTrack.id)
          } else {
            likedIds.delete(track.parentTrack.id)
          }
        }
      }

      if (track.remixes && track.remixes.length > 0) {
        remixesMap[track.id] = track.remixes
        for (const remix of track.remixes) {
          if (remix.isLiked !== undefined) {
            if (remix.isLiked) {
              likedIds.add(remix.id)
            } else {
              likedIds.delete(remix.id)
            }
          }
        }
      }

      if (track.isLiked !== undefined) {
        if (track.isLiked) {
          likedIds.add(track.id)
        } else {
          likedIds.delete(track.id)
        }
      }
    }

    setTrackParents(parentsMap)
    setTrackRemixes(remixesMap)
    setLikedTrackIds(likedIds)
  }

  useEffect(() => {
    if (tracks.length > 0 && !isTogglingLikeRef.current) {
      loadTrackData(tracks)
    }
  }, [tracks])

  const handleLoad = (track: Track) => {
    const wasPlaying = activeTrackId === track.id && actuallyPlaying
    sessionStore.setRemixTrack({
      code: track.code,
      trackId: track.id,
      title: track.title,
      artist: track.userName || 'Unknown',
      wasPlaying,
      trackData: track,
    })
    sessionStore.setSkipExampleOnce(true)
    navigate('/editor')
  }

  const handleLoadInA = (track: Track) => {
    const wasPlaying = activeTrackId === track.id && actuallyPlaying
    const isCurrentlyPlaying = dspscript.isPlaying()
    const autoCompileAndPlay = isCurrentlyPlaying && channelMode === 'dual'
    sessionStore.setRemixTrack({
      code: track.code,
      trackId: track.id,
      title: track.title,
      artist: track.userName || 'Unknown',
      loadIn: 'A',
      wasPlaying,
      autoCompileAndPlay,
      trackData: track,
    })
    sessionStore.setSkipExampleOnce(true)
    navigate('/editor')
  }

  const handleLoadInB = (track: Track) => {
    const wasPlaying = activeTrackId === track.id && actuallyPlaying
    const isCurrentlyPlaying = dspscript.isPlaying()
    sessionStore.setRemixTrack({
      code: track.code,
      trackId: track.id,
      title: track.title,
      artist: track.userName || 'Unknown',
      loadIn: 'B',
      wasPlaying,
      autoCompileAndPlay: isCurrentlyPlaying && channelMode === 'dual',
      trackData: track,
    })
    sessionStore.setSkipExampleOnce(true)
    navigate('/editor')
  }

  const handleToggleLike = async (track: Track) => {
    if (!user) return

    if (track.userId === user.id) return

    const isLiked = likedTrackIds.has(track.id)
    isTogglingLikeRef.current = true
    try {
      if (isLiked) {
        setLikedTrackIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(track.id)
          return newSet
        })
        if (onTracksUpdate) {
          onTracksUpdate(
            tracks.map(t =>
              t.id === track.id
                ? { ...t, likesCount: Math.max(0, t.likesCount - 1), isLiked: false }
                : t,
            ),
          )
        }
        if (onSingleTrackUpdate && tracks.length === 1 && tracks[0]?.id === track.id) {
          onSingleTrackUpdate({
            ...tracks[0],
            likesCount: Math.max(0, tracks[0].likesCount - 1),
            isLiked: false,
          })
        }
        setTrackRemixes(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            updated[key] = updated[key].map(r =>
              r.id === track.id
                ? { ...r, likesCount: Math.max(0, r.likesCount - 1), isLiked: false }
                : r,
            )
          }
          return updated
        })
        setTrackParents(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            if (updated[key].id === track.id) {
              updated[key] = {
                ...updated[key],
                likesCount: Math.max(0, updated[key].likesCount - 1),
                isLiked: false,
              }
            }
          }
          return updated
        })
        await api.tracks.unlike(track.id)
      } else {
        setLikedTrackIds(prev => new Set(prev).add(track.id))
        if (onTracksUpdate) {
          onTracksUpdate(
            tracks.map(t =>
              t.id === track.id ? { ...t, likesCount: t.likesCount + 1, isLiked: true } : t,
            ),
          )
        }
        if (onSingleTrackUpdate && tracks.length === 1 && tracks[0]?.id === track.id) {
          onSingleTrackUpdate({ ...tracks[0], likesCount: tracks[0].likesCount + 1, isLiked: true })
        }
        setTrackRemixes(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            updated[key] = updated[key].map(r =>
              r.id === track.id ? { ...r, likesCount: r.likesCount + 1, isLiked: true } : r,
            )
          }
          return updated
        })
        setTrackParents(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            if (updated[key].id === track.id) {
              updated[key] = {
                ...updated[key],
                likesCount: updated[key].likesCount + 1,
                isLiked: true,
              }
            }
          }
          return updated
        })
        await api.tracks.like(track.id)
      }
    } catch (err: any) {
      showToast(err.message)
      if (isLiked) {
        setLikedTrackIds(prev => new Set(prev).add(track.id))
        if (onTracksUpdate) {
          onTracksUpdate(
            tracks.map(t =>
              t.id === track.id ? { ...t, likesCount: t.likesCount + 1, isLiked: true } : t,
            ),
          )
        }
        if (onSingleTrackUpdate && tracks.length === 1 && tracks[0]?.id === track.id) {
          onSingleTrackUpdate({ ...tracks[0], likesCount: tracks[0].likesCount + 1, isLiked: true })
        }
        setTrackRemixes(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            updated[key] = updated[key].map(r =>
              r.id === track.id ? { ...r, likesCount: r.likesCount + 1, isLiked: true } : r,
            )
          }
          return updated
        })
        setTrackParents(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            if (updated[key].id === track.id) {
              updated[key] = {
                ...updated[key],
                likesCount: updated[key].likesCount + 1,
                isLiked: true,
              }
            }
          }
          return updated
        })
      } else {
        setLikedTrackIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(track.id)
          return newSet
        })
        if (onTracksUpdate) {
          onTracksUpdate(
            tracks.map(t =>
              t.id === track.id
                ? { ...t, likesCount: Math.max(0, t.likesCount - 1), isLiked: false }
                : t,
            ),
          )
        }
        if (onSingleTrackUpdate && tracks.length === 1 && tracks[0]?.id === track.id) {
          onSingleTrackUpdate({
            ...tracks[0],
            likesCount: Math.max(0, tracks[0].likesCount - 1),
            isLiked: false,
          })
        }
        setTrackRemixes(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            updated[key] = updated[key].map(r =>
              r.id === track.id
                ? { ...r, likesCount: Math.max(0, r.likesCount - 1), isLiked: false }
                : r,
            )
          }
          return updated
        })
        setTrackParents(prev => {
          const updated = { ...prev }
          for (const key in updated) {
            if (updated[key].id === track.id) {
              updated[key] = {
                ...updated[key],
                likesCount: Math.max(0, updated[key].likesCount - 1),
                isLiked: false,
              }
            }
          }
          return updated
        })
      }
    } finally {
      // Reset the flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isTogglingLikeRef.current = false
      }, 100)
    }
  }

  const handlePlayRemix = async (remix: Track) => {
    try {
      setActiveTrackId(null)
      setActiveRemixId(remix.id)
      setActiveRemixIdInStore(remix.id)
      removeActiveTrackId()
      sessionStore.removeEditorInitiatedPlayback()
      sessionStore.removeEditorPlayingTabId()

      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      // Stop any currently playing audio before loading new program
      if (dspscript.isPlaying()) {
        await dspscript.stop()
      }

      await dspscript.loadProgram(remix.code)
      await dspscript.start()
      setActuallyPlaying(true)
    } catch (err: any) {
      showToast(err.message)
      setActiveRemixId(null)
      removeActiveRemixId()
    }
  }

  const handleStopRemix = async () => {
    try {
      await dspscript.stop()
      setActiveRemixId(null)
      setActuallyPlaying(false)
      removeActiveRemixId()
    } catch (err: any) {
      showToast(err.message)
    }
  }

  const handleNavBarPlay = async () => {
    if (dspscript.isPlaying()) {
      return
    }

    if (activeTrackId || activeRemixId) {
      return
    }

    if (tracks.length === 0 || loading) {
      return
    }

    const storedActiveTrackId = getActiveTrackId()
    const storedActiveRemixId = getActiveRemixId()

    let trackToPlay: Track | null = null
    let isRemix = false

    if (storedActiveRemixId) {
      const remix = Object.values(trackRemixes)
        .flat()
        .find(r => r.id === storedActiveRemixId)
      if (remix) {
        trackToPlay = remix
        isRemix = true
      }
    }

    if (!trackToPlay && storedActiveTrackId) {
      const track = tracks.find(t => t.id === storedActiveTrackId)
      if (track) {
        trackToPlay = track
      }
    }

    if (!trackToPlay) {
      trackToPlay = tracks[0]
      isRemix = false
    }

    if (!trackToPlay) return

    try {
      if (isRemix) {
        setActiveTrackId(null)
        setActiveRemixId(trackToPlay.id)
        setActiveRemixIdInStore(trackToPlay.id)
        removeActiveTrackId()
      } else {
        setActiveTrackId(trackToPlay.id)
        setActiveRemixId(null)
        setActiveTrackIdInStore(trackToPlay.id)
        removeActiveRemixId()
      }
      sessionStore.removeEditorInitiatedPlayback()
      sessionStore.removeEditorPlayingTabId()

      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      // Stop any currently playing audio before loading new program
      if (dspscript.isPlaying()) {
        await dspscript.stop()
      }

      await dspscript.loadProgram(trackToPlay.code)
      await dspscript.start()
      setActuallyPlaying(true)
    } catch (err: any) {
      showToast(err.message)
      setActiveTrackId(null)
      setActiveRemixId(null)
      removeActiveTrackId()
      removeActiveRemixId()
    }
  }

  const handleNavBarStop = async () => {
    try {
      await dspscript.stop()
      setActuallyPlaying(false)
      setActiveTrackId(null)
      setActiveRemixId(null)
    } catch (err: any) {
      showToast(err.message)
    }
  }

  return {
    activeTrackId,
    actuallyPlaying,
    trackParents,
    trackRemixes,
    likedTrackIds,
    activeRemixId,
    compileMode,
    setActiveTrackId: (id: string | null) => {
      setActiveTrackId(id)
      if (id) {
        setActiveTrackIdInStore(id)
        removeActiveRemixId()
      } else {
        removeActiveTrackId()
      }
    },
    setActiveRemixId: (id: string | null) => {
      setActiveRemixId(id)
      if (id) {
        setActiveRemixIdInStore(id)
        removeActiveTrackId()
      } else {
        removeActiveRemixId()
      }
    },
    setActuallyPlaying,
    handleLoad,
    handleLoadInA,
    handleLoadInB,
    handleToggleLike,
    handlePlayRemix,
    handleStopRemix,
    handleNavBarPlay,
    handleNavBarStop,
    loadTrackData,
  }
}
